import { useEffect, useState } from "react";
import ResourcePage from "@/components/common/ResourcePage";
import { useMeta } from "@/context/MetaContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function RevenueSummary() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/sales/revenue/summary")
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => console.error("Revenue summary failed", e));
    return () => { alive = false; };
  }, []);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/sales/revenue/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "revenue.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const cells = [
    { label: "Gross Revenue", value: data?.gross },
    { label: "Net Revenue", value: data?.net },
    { label: "Discounts", value: data?.discount },
    { label: "Tax Collected", value: data?.tax },
  ];

  return (
    <div className="rounded-sm border border-border bg-card p-5" data-testid="revenue-summary">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-base font-bold tracking-tight">
          <TrendingUp className="h-4 w-4 text-primary" /> Revenue Overview
        </h3>
        {can("revenue:export") && (
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={busy} data-testid="revenue-export-btn">
            <Download className="mr-1.5 h-4 w-4" /> {busy ? "Exporting…" : "Export CSV"}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label}>
            <div className="eyebrow">{c.label}</div>
            <div className="mt-1 font-display text-xl font-bold tabular">{data ? money(c.value) : "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-muted-foreground">{data ? `${data.count} sales record${data.count === 1 ? "" : "s"}` : "Loading…"}</div>
    </div>
  );
}

export default function Sales() {
  const { options, outletOptions, outletName } = useMeta();
  const { user, can } = useAuth();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";

  const fields = [
    { name: "date", label: "Date", type: "date", span: 1, required: true, default: new Date().toISOString().slice(0, 10) },
    ...(isSuper ? [{ name: "outlet_id", label: "Outlet", type: "select", span: 1, options: outletOptions, required: true }] : []),
    { name: "category", label: "Category", type: "select", span: 1, options: options("sales_category"), required: true },
    { name: "channel", label: "Channel", type: "select", span: 1, options: options("sales_channel"), required: true },
    { name: "gross_amount", label: "Gross Amount", type: "number", span: 1, step: "0.01", required: true },
    { name: "discount", label: "Discount", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "tax", label: "Tax", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "refunds", label: "Refunds / Voids", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "payment_method", label: "Payment Method", type: "select", span: 1, options: options("payment_method") },
    { name: "notes", label: "Notes", type: "textarea" },
  ];

  const columns = [
    { key: "date", label: "Date", sortable: true, render: (r) => <span className="tabular">{r.date}</span> },
    { key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) },
    { key: "category", label: "Category", render: (r) => <span className="font-medium">{r.category}</span> },
    { key: "channel", label: "Channel", render: (r) => <StatusBadge status={String(r.channel).toLowerCase()} /> },
    { key: "gross_amount", label: "Gross", sortable: true, cellClassName: "tabular text-right", className: "text-right", render: (r) => money(r.gross_amount) },
    { key: "discount", label: "Disc.", cellClassName: "tabular text-right", className: "text-right", render: (r) => money(r.discount) },
    { key: "net_amount", label: "Net", sortable: true, cellClassName: "tabular text-right font-semibold", className: "text-right", render: (r) => money(r.net_amount) },
    { key: "payment_method", label: "Payment", render: (r) => r.payment_method || "—" },
  ];

  return (
    <div className="space-y-6">
      {can("revenue:view") && <RevenueSummary />}
      <ResourcePage
        title="Sales / Revenue"
        subtitle="Category & channel-wise sales with discounts, tax and refunds."
        endpoint="sales"
        module="sales"
        columns={columns}
        fields={fields}
        searchPlaceholder="Search category, channel, notes…"
        emptyHint="Record your first sale to start tracking revenue."
      />
    </div>
  );
}
